import { AfterViewInit, Component, Input, OnDestroy, ViewChild, HostListener } from '@angular/core';
import { MatTableModule } from '@angular/material/table';
// Use relative imports within the same library (libs/shared-lib-platform)
import { ConfigService } from '../../shared-services/config.service';
import { CommonModule } from '@angular/common';

import { GlobalFilterService } from '../../shared-services/global-filter-service';
import { ChartFilterHandlerService } from '../../shared-services/chart-filter-handler.service';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, switchMap, tap, takeUntil } from 'rxjs/operators';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { PageEvent } from '@angular/material/paginator';
// use shared ConfigService from library
import { HttpClient } from '@angular/common/http';
import { PaginatorComponent } from '../../global-components/paginator/paginator.component';

// Import utility functions
import {
  matchValue,
  findMatchingCategories,
  buildCategoryFilterData,
  type MatchMode,
} from './../../utils/filter.utils';

import { paginate } from './../../utils/data-transform.utils';
import {
  isFrozenColumn as _isFrozenColumn,
  isLastFrozenColumn as _isLastFrozenColumn,
  getFrozenColumnLeft as _getFrozenColumnLeft,
  isFrozenGroup as _isFrozenGroup,
  getFrozenGroupLeft as _getFrozenGroupLeft,
  isFrozenBodyRow as _isFrozenBodyRow,
  getFrozenBodyRowTop as _getFrozenBodyRowTop,
  getFrozenBodyRowBottom as _getFrozenBodyRowBottom,
} from './../../utils/table-freeze.utils';

@Component({
  selector: 'generic-table',
  standalone: true,
  imports: [MatTableModule, CommonModule, MatProgressSpinnerModule, PaginatorComponent],
  templateUrl: './generic-table.html',
  styleUrl: './generic-table.scss',
})
export class GenericTableComponent implements AfterViewInit, OnDestroy {
  private readonly documentMouseMoveHandler = (event: MouseEvent): void => {
    this.onMouseMove(event);
  };

  private readonly documentMouseUpHandler = (): void => {
    this.onMouseUp();
  };

  @Input() data!: any;
  displayedColumns: string[] = [];
  columnWidths: { [col: string]: number } = {};
  protected readonly DEFAULT_COL_WIDTH: number = 100;
  protected readonly MIN_COL_WIDTH: number = 16;
  protected resizingCol: string | null = null;
  protected resizeStartX = 0;
  protected resizeStartWidth = 0;
  protected suppressNextHeaderClick = false;
  filter: any;
  isLoading = true;
  columnLabels: { [key: string]: string } = {};
  dataSource: any[] = [];
  protected unSubscribe$: Subject<void> = new Subject();
  @Input() set widgetId(val: string) {
    this._widgetId = val;
  }
  get widgetId(): string {
    return this._widgetId || this.data?.widgetId;
  }
  protected _widgetId!: string;

  get tableHeight(): string {
    return (
      this.data?.heightContainer || this.data?.height || this.data?.chartConfig?.height || 'auto'
    );
  }

  pagedData: any[] = [];
  protected originalDataSource: any[] = [];
  activeLegend: string | null = null;

  protected pageSize!: number;
  protected pageIndex: number = 0;

  // Cell selection properties (supports multiple selection with Ctrl+click)
  selectedCells: { row: any; column: string }[] = [];

  // Grouped header properties
  headerGroups: { key: string; label: string; colspan: number; columns: string[] }[] = [];
  groupHeaderDisplayedColumns: string[] = [];
  hasGroupedHeaders = false;

  // Column collapse/expand properties
  collapsedColumns = new Set<string>();
  collapsedGroups = new Set<string>();
  private preCollapseWidths: { [col: string]: number } = {};
  protected readonly DEFAULT_COLLAPSED_COL_WIDTH = 24;

  // Header selection properties (for clickable headers)
  selectedGroupHeaders: Set<string> = new Set();
  selectedColumnHeaders: Set<string> = new Set();

  // ─── Freeze Row / Column (delegated to shared utility) ─────────────────────

  // Cross-filtering properties
  protected legendFilterSub!: Subscription;
  // Stores the last active legendSync filter so it can be re-applied after a data reload
  protected activeLegendSyncFilter: {
    sourceWidgetId: string;
    selectedLegends: string[];
    selectedIndices: number[];
    selectedColors: string[];
    targetWidgetIds: string[];
    visitedWidgetIds: string[];
    isCategoryFilter: boolean;
  } | null = null;

  @ViewChild('paginatorComp') paginatorComp!: PaginatorComponent;

  public isQuarterlyIdentifierColumn(column: string): boolean {
    return (
      this.data?.enableQuarterlyPivot === true &&
      (column === 'customer_name' || column === 'channel_type')
    );
  }

  public isQuarterlyIdentifierGroup(group: { columns?: string[] } | null | undefined): boolean {
    if (this.data?.enableQuarterlyPivot !== true) return false;
    const cols = group?.columns || [];
    return cols.length === 1 && this.isQuarterlyIdentifierColumn(cols[0]);
  }

  /**
   * Calculates the left offset for sticky columns.
   * Only applicable when enableQuarterlyPivot is true.
   */
  public getStickyLeftOffset(column: string): number {
    if (!this.isQuarterlyIdentifierColumn(column)) return 0;

    const stickyCols = ['customer_name', 'channel_type'];
    const colIndex = stickyCols.indexOf(column);
    if (colIndex <= 0) return 0;

    let offset = 0;
    for (let i = 0; i < colIndex; i++) {
      const prevCol = stickyCols[i];
      offset += this.columnWidths[prevCol] || this.DEFAULT_COL_WIDTH;
    }
    return offset;
  }

  public isSingleQuarterYearGroup(
    group: { label?: string; columns?: string[] } | null | undefined,
  ): boolean {
    if (this.data?.enableQuarterlyPivot !== true) return false;
    const label = String(group?.label || '');
    const cols = group?.columns || [];
    return /^\d{4}$/.test(label) && cols.length === 1;
  }

  public getGroupWidthPx(group: { columns?: string[] } | null | undefined): number {
    const cols = group?.columns || [];
    if (!cols.length) return this.DEFAULT_COL_WIDTH;
    return cols.reduce(
      (sum, col) => sum + (this.columnWidths[col] ?? this.DEFAULT_COL_WIDTH),
      0,
    );
  }

  constructor(
    protected configService: ConfigService,
    protected globalFilterService: GlobalFilterService,
    protected chartFilterHandler: ChartFilterHandlerService,
    protected http: HttpClient
  ) {
    this.legendFilterSub = this.globalFilterService.filter$.subscribe((filters: any) => {
      // Skip if data not yet available
      if (!this.data?.widgetId) return;

      // Check if this widget should respond to the filter
      if (this.chartFilterHandler.shouldReload(filters, this.data.widgetId)) {
        if (filters?.legendSync) {
          this.handleLegendSync(filters);
          // For category filters (label clicks), also trigger API reload
          if (filters.isCategoryFilter) {
            this.loadAndRenderChart(filters);
          }
        } else {
          console.log('filters 22', filters);
          this.loadAndRenderChart(filters);
        }
      }
    });
  }

  // Renamed from subscribeToCrossFilterEvents logic
  protected handleLegendSync(filters: any): void {
    // Build event object for handleExternalLegendFilter
    const filterListener = filters.filterListener || '';
    const listeners = filterListener.split(',').map((s: string) => s.trim());

    // Get legend names from indices if not provided directly
    let selectedLegends = filters.selectedLegends || [];
    if (
      selectedLegends.length === 0 &&
      filters.selectedIndices?.length > 0 &&
      filters.seriesNames
    ) {
      selectedLegends = filters.selectedIndices
        .map((i: number) => filters.seriesNames[i])
        .filter(Boolean);
    }

    this.handleExternalLegendFilter({
      sourceWidgetId: filters.sourceWidgetId,
      selectedLegends: selectedLegends,
      selectedIndices: filters.selectedIndices || [],
      selectedColors: filters.selectedColors || [],
      targetWidgetIds: listeners,
      visitedWidgetIds: filters.visitedWidgetIds || [],
      isCategoryFilter: filters.isCategoryFilter || false,
    });
  }

  ngAfterViewInit(): void {
    // Initial load
    setTimeout(() => this.loadAndRenderChart(), 0);
  }

  // Initialize table with inline data (for POC/mock data)
  protected initTableWithData(tableData: any[]): void {
    this.isLoading = false;
    // Set pageSize from config if provided
    if (this.data?.pageSize) {
      this.pageSize = this.data.pageSize;
    }
    this.originalDataSource = tableData;
    this.dataSource = [...this.originalDataSource];

    if (!!this.data?.isPagination) {
      this.setPagedData();
    }

    this.displayedColumns = this.data?.headerColumns?.map((col: any) => col.key) || [];
    this.columnLabels = Object.fromEntries(
      this.data?.headerColumns?.map((col: any) => [col.key, col.label]) || [],
    );

    this.initColumnWidths();

    // Initialize grouped headers if configured
    this.initGroupedHeaders();

    setTimeout(() => {
      this.paginatorComp?.updatePagination(
        this.dataSource.length,
        this.pageIndex,
        // this.pageSize
      );
    }, 200);
  }

  // Initialize grouped headers from config
  protected initGroupedHeaders(): void {
    if (this.data?.headerGroups && Array.isArray(this.data.headerGroups)) {
      this.hasGroupedHeaders = true;
      this.headerGroups = this.data.headerGroups.map((group: any, index: number) => ({
        key: `group-${index}`,
        label: group.label,
        colspan: group.columns?.length || 1,
        columns: group.columns || [],
      }));
      this.groupHeaderDisplayedColumns = this.headerGroups.map((g) => g.key);
      console.log(
        '[Base GenericTableComponent] initGroupedHeaders - widgetId:',
        this.widgetId,
        'headerGroups:',
        this.headerGroups,
        'hasGroupedHeaders:',
        this.hasGroupedHeaders,
      );
    } else {
      this.hasGroupedHeaders = false;
      this.groupHeaderDisplayedColumns = [];
      console.log(
        '[Base GenericTableComponent] initGroupedHeaders - widgetId:',
        this.widgetId,
        'NO headerGroups configured. hasGroupedHeaders set to false.',
      );
    }
  }

  // Helper to check row equality by content if reference differs
  private isSameRow(row1: any, row2: any): boolean {
    if (row1 === row2) return true;
    if (!row1 || !row2) return false;

    // Use keys from config or headers to identify uniqueness
    let keys = this.data?.filterKey;
    if (!keys || keys.length === 0) {
      keys = this.data?.headerColumns?.map((col: any) => col.key);
    }

    if (keys && keys.length > 0) {
      // Compare values for identification keys
      return keys.every((key: string) => {
        const val1 = row1[key] !== undefined && row1[key] !== null ? String(row1[key]).trim() : '';
        const val2 = row2[key] !== undefined && row2[key] !== null ? String(row2[key]).trim() : '';
        return val1 === val2;
      });
    }

    return false;
  }

  // Cell selection methods (supports Ctrl+click for multiple selection)
  onCellClick(row: any, column: string, event: MouseEvent): void {
    const cellIndex = this.selectedCells.findIndex(
      (cell) => this.isSameRow(cell.row, row) && cell.column === column,
    );
    const isAlreadySelected = cellIndex !== -1;

    if (event.ctrlKey || event.metaKey) {
      // Ctrl+click: toggle selection (add or remove from selection)
      if (isAlreadySelected) {
        this.selectedCells.splice(cellIndex, 1);
      } else {
        this.selectedCells.push({ row, column });
      }
    } else {
      // Normal click: single selection
      if (isAlreadySelected && this.selectedCells.length === 1) {
        // Deselect if clicking the only selected cell
        this.selectedCells = [];
      } else {
        // Select only this cell
        this.selectedCells = [{ row, column }];
      }
    }

    // Update filters based on ALL currently selected cells (multi-select aware)
    this.updateFilterFromSelectedCells();

    // Also emit legendSync reset to other tables when nothing is selected
    if (this.selectedCells.length === 0) {
      this.emitLegendFilter([]);
    }
  }

  // Update filter selection (aligned with StackBarChartComponent)
  protected updateFilterSelection(data: any): void {
    this.chartFilterHandler.updateFilterSelection(this.data, data);
  }

  /**
   * Rebuilds requestParams from the current selectedCells state.
   * Groups cell values by their API attribute key (via columnFilterKeyMap).
   * Supports multi-select: multiple cells for the same key accumulate into an array.
   * Clears keys from requestParams that are no longer in selectedCells.
   */
  protected updateFilterFromSelectedCells(): void {
    if (!this.data?.filterKey) return;

    const columnFilterKeyMap: { [col: string]: string } = this.data?.columnFilterKeyMap || {};
    const listener = this.data?.filterListener || this.data?.filterListner;
    const listenerStr = Array.isArray(listener) ? listener.join(',') : listener || '';
    const widgetId = this.data?.widgetId;

    // All possible API keys this table manages (for cleanup)
    const allFilterKeys: string[] = Array.isArray(this.data.filterKey)
      ? this.data.filterKey
      : [this.data.filterKey];

    if (this.selectedCells.length === 0) {
      // Build a reset listener that includes all target charts AND this table itself,
      // mirroring how stackbar legendListener includes itself so shouldReload returns
      // true for the emitting widget on deselection.
      const listenerParts = listenerStr ? listenerStr.split(',').map((s: string) => s.trim()) : [];
      const selfId = String(widgetId || '').trim();
      if (selfId && !listenerParts.includes(selfId)) {
        listenerParts.push(selfId);
      }
      const resetListener = listenerParts.length > 0 ? listenerParts : undefined;

      // Clear all managed filter keys from requestParams
      allFilterKeys.forEach((key, index) => {
        const isLast = index === allFilterKeys.length - 1;
        if (isLast) {
          this.globalFilterService.resetFilterSelection(key, false, resetListener);
        } else {
          this.globalFilterService.deleteKey(key, false);
        }
      });
      return;
    }

    // Build apiKey -> Set<value> from all selected cells
    const filterMap = new Map<string, Set<any>>();
    const identifierKeys: string[] = Array.isArray(this.data.filterKey)
      ? this.data.filterKey
      : [this.data.filterKey];
    // Respect configured total key (defaults to 'TOTAL') - do not include it as a filter attribute
    const totalKey = (this.data && this.data.totalKey) || 'TOTAL';

    for (const cell of this.selectedCells) {
      const apiKey = columnFilterKeyMap[cell.column] || cell.column;

      const isTotalColumn = apiKey === totalKey || cell.column === totalKey;
      if (!isTotalColumn) {
        if (!filterMap.has(apiKey)) {
          filterMap.set(apiKey, new Set());
        }
        // If the column maps to a DIFFERENT API key (e.g. "2019" -> "year"),
        // use the column name itself as the value ("2019"), not the cell data.
        // Otherwise use the actual cell value.
        const val = apiKey !== cell.column ? cell.column : cell.row[cell.column];
        if (val !== null && val !== undefined && val !== '') {
          filterMap.get(apiKey)!.add(val);
        }
      }
      // If the column maps to a DIFFERENT API key (e.g. "2019" -> "year"),
      // use the column name itself as the value ("2019"), not the cell data.
      // Otherwise use the actual cell value.
      // const val = (apiKey !== cell.column) ? cell.column : cell.row[cell.column];
      // if (val !== null && val !== undefined && val !== '') {
      //   filterMap.get(apiKey)!.add(val);
      // }
      // Also include identifier values from the selected row (e.g. customer_name, channel_type)
      // so that selecting a year cell will also pass row identifiers alongside the year.
      for (const idKey of identifierKeys) {
        // don't duplicate if idKey is the same as apiKey (already handled)
        if (idKey === apiKey) continue;
        const idVal = cell.row?.[idKey];
        if (idVal !== null && idVal !== undefined && idVal !== '') {
          if (!filterMap.has(idKey)) {
            filterMap.set(idKey, new Set());
          }
          filterMap.get(idKey)!.add(idVal);
        }
      }
    }

    // Clear keys no longer active
    allFilterKeys.forEach((key) => {
      if (!filterMap.has(key)) {
        this.globalFilterService.deleteKey(key, false);
      }
    });

    // Set each active key with its accumulated values
    // On SELECTION: exclude self from listener so the table does NOT call its own API.
    // (Same as stackbar legend: selecting a legend doesn't reload the chart itself.)
    const selfId = String(widgetId || '').trim();
    const selectionListenerParts = (listenerStr || '')
      .split(',')
      .map((s: string) => s.trim())
      .filter((s: string) => s && s !== selfId);
    const selectionListener = selectionListenerParts.length > 0
      ? selectionListenerParts.join(',')
      : undefined;

    const activeKeys = Array.from(filterMap.keys());
    activeKeys.forEach((key, index) => {
      const values = Array.from(filterMap.get(key)!);
      const isLast = index === activeKeys.length - 1;
      this.globalFilterService.updateFilterSelection(
        key,
        values,
        false,
        widgetId,
        isLast,
        selectionListener,
      );
    });
  }

  // Collect all filter values from selected cells (uses crossFilterColumns config)
  protected emitFilterFromSelectedCells(): void {
    const filterValues: string[] = [];
    // Get crossFilterColumns from config, default to empty array
    const crossFilterColumns: string[] = this.data?.crossFilterColumns || [];
    const identifierKeys: string[] = Array.isArray(this.data.filterKey)
      ? this.data.filterKey
      : [this.data.filterKey];

    this.selectedCells.forEach((cell) => {
      const row = cell.row;

      // Add values from all configured crossFilterColumns
      crossFilterColumns.forEach((colKey) => {
        const value = row[colKey];
        if (value && !filterValues.includes(value)) {
          filterValues.push(value);
        }
      });

      // Also add the clicked cell value if not already included
      const cellValue = row[cell.column];
      if (cellValue && !filterValues.includes(cellValue)) {
        filterValues.push(cellValue);
      }

      // Also include identifier values (customer_name, channel_type, etc.) so
      // cross-widget filters receive row identifiers along with the clicked cell.
      identifierKeys.forEach((idKey) => {
        const idVal = row?.[idKey];
        if (idVal && !filterValues.includes(idVal)) {
          filterValues.push(idVal);
        }
      });
    });

    this.emitLegendFilter(filterValues);
  }

  isCellSelected(row: any, column: string): boolean {
    return this.selectedCells.some(
      (cell) => this.isSameRow(cell.row, row) && cell.column === column,
    );
  }

  // Group header click handler (e.g., 2023, 2024, 2025)
  onGroupHeaderClick(
    group: { label: string; colspan: number; columns: string[] },
    event: MouseEvent,
  ): void {
    if (!group.label) return; // Don't handle empty labels

    if (event.ctrlKey || event.metaKey) {
      // Ctrl+click: toggle selection
      if (this.selectedGroupHeaders.has(group.label)) {
        this.selectedGroupHeaders.delete(group.label);
      } else {
        this.selectedGroupHeaders.add(group.label);
      }
    } else {
      // Normal click: single selection
      if (this.selectedGroupHeaders.size === 1 && this.selectedGroupHeaders.has(group.label)) {
        this.selectedGroupHeaders.clear();
      } else {
        this.selectedGroupHeaders.clear();
        this.selectedGroupHeaders.add(group.label);
      }
    }

    // Clear other selections
    this.selectedCells = [];
    this.selectedColumnHeaders.clear();

    // Emit filter with group label and all column keys in this group
    this.emitHeaderFilter();
  }

  isGroupHeaderSelected(label: string): boolean {
    return this.selectedGroupHeaders.has(label);
  }

  // Column header click handler (e.g., Q4, Q1, Q2)
  onColumnHeaderClick(column: string, event: MouseEvent): void {
    if (this.suppressNextHeaderClick) {
      this.suppressNextHeaderClick = false;
      return;
    }

    if (event.ctrlKey || event.metaKey) {
      // Ctrl+click: toggle selection
      if (this.selectedColumnHeaders.has(column)) {
        this.selectedColumnHeaders.delete(column);
      } else {
        this.selectedColumnHeaders.add(column);
      }
    } else {
      // Normal click: single selection
      if (this.selectedColumnHeaders.size === 1 && this.selectedColumnHeaders.has(column)) {
        this.selectedColumnHeaders.clear();
      } else {
        this.selectedColumnHeaders.clear();
        this.selectedColumnHeaders.add(column);
      }
    }

    // Clear other selections
    this.selectedCells = [];
    this.selectedGroupHeaders.clear();

    // Emit filter with column label
    this.emitHeaderFilter();
  }

  isColumnHeaderSelected(column: string): boolean {
    return this.selectedColumnHeaders.has(column);
  }

  // Check if a column should be highlighted (either directly selected or part of a selected group)
  isColumnHighlighted(column: string): boolean {
    // Check if column is directly selected
    if (this.selectedColumnHeaders.has(column)) {
      return true;
    }

    // Check if column belongs to a selected group header
    for (const groupLabel of Array.from(this.selectedGroupHeaders)) {
      const group = this.headerGroups.find((g) => g.label === groupLabel);
      if (group && group.columns && group.columns.includes(column)) {
        return true;
      }
    }

    return false;
  }

  // ─── Column Collapse / Expand ──────────────────────────────────────────────

  toggleColumnCollapse(column: string, event: MouseEvent): void {
    event.stopPropagation();
    if (this.collapsedColumns.has(column)) {
      this.collapsedColumns.delete(column);
      this.columnWidths[column] = this.preCollapseWidths[column] || this.DEFAULT_COL_WIDTH;
    } else {
      this.preCollapseWidths[column] = this.columnWidths[column] || this.DEFAULT_COL_WIDTH;
      this.collapsedColumns.add(column);
      this.columnWidths[column] = this.getCollapsedColumnWidth();
    }
  }

  isColumnCollapsed(column: string): boolean {
    return this.collapsedColumns.has(column);
  }

  toggleGroupCollapse(group: { label: string; columns: string[] }, event: MouseEvent): void {
    event.stopPropagation();
    if (!group.label) return;
    if (this.collapsedGroups.has(group.label)) {
      this.collapsedGroups.delete(group.label);
      group.columns.forEach((col) => {
        this.collapsedColumns.delete(col);
        this.columnWidths[col] = this.preCollapseWidths[col] || this.DEFAULT_COL_WIDTH;
      });
    } else {
      this.collapsedGroups.add(group.label);
      group.columns.forEach((col) => {
        this.preCollapseWidths[col] = this.columnWidths[col] || this.DEFAULT_COL_WIDTH;
        this.collapsedColumns.add(col);
        this.columnWidths[col] = this.getCollapsedColumnWidth();
      });
    }
  }

  isGroupCollapsed(group: { label: string; columns: string[] }): boolean {
    if (!group.label || !group.columns?.length) return false;
    return this.collapsedGroups.has(group.label);
  }

  /**
   * Get the match mode from config
   */
  protected getMatchMode(): MatchMode {
    return this.data?.categoryMatchMode || 'contains';
  }

  // Emit filter based on selected headers
  // Sends normalized column names (e.g. "Q1 2025") so charts and tables match consistently
  protected emitHeaderFilter(): void {
    const filterValues: string[] = [];

    // For group headers, expand to all normalized column names under the group
    // e.g. clicking "2025" → sends ["Q1 2025", "Q2 2025", "Q3 2025", "Q4 2025"]
    this.selectedGroupHeaders.forEach((label) => {
      const group = this.headerGroups.find((g) => g.label === label);
      if (group?.columns) {
        group.columns.forEach((col) => {
          const normalizedCol = col.replace(/_/g, ' ');
          if (!filterValues.includes(normalizedCol)) {
            filterValues.push(normalizedCol);
          }
        });
      }
    });

    // For column headers, send normalized column key
    // e.g. clicking "Q1_2025" header → sends ["Q1 2025"]
    this.selectedColumnHeaders.forEach((column) => {
      const normalizedCol = column.replace(/_/g, ' ');
      if (!filterValues.includes(normalizedCol)) {
        filterValues.push(normalizedCol);
      }
    });

    if (filterValues.length === 0) {
      this.emitLegendFilter([]);
    } else {
      this.emitLegendFilter(filterValues);
    }
  }

  // Emit legend filter to other widgets (charts and tables)
  protected emitLegendFilter(selectedLegends: string[]): void {
    const filterListeners = this.data?.filterListner || this.data?.filterListener || [];
    if (filterListeners.length === 0) return;

    const widgetId = this.data?.widgetId;
    if (!widgetId) return;

    // Determine if this is a category-based filter (year/quarter headers)
    const isCategoryFilter =
      this.selectedGroupHeaders.size > 0 || this.selectedColumnHeaders.size > 0;

    // Use utility for category-based filter
    if (isCategoryFilter) {
      const labelKey = this.data?.labelKey || this.data?.legendKey || this.data?.filterKey;
      const selfId = String(widgetId || '').trim();
      const rawListenerStr = Array.isArray(filterListeners) ? filterListeners.join(',') : filterListeners;
      let activeListener: string | undefined;

      // Set filter values in requestParams so listening charts include them in API payload
      if (selectedLegends.length > 0) {
        // --- API CALL LOGIC ALIGNMENT (from cell selection) ---
        // Exclude self from listener so table does NOT call its own API on selection.
        const selectionListenerParts = rawListenerStr
          .split(',')
          .map((s: string) => s.trim())
          .filter((s: string) => s && s !== selfId);
        activeListener = selectionListenerParts.length > 0 ? selectionListenerParts.join(',') : undefined;

        // Set the primary labelKey with individual categories (e.g., ["Q1 2025", "Q2 2025"])
        if (labelKey) {
          this.globalFilterService.updateFilterSelection(
            labelKey,
            selectedLegends,
            false,
            widgetId,
            false, // don't emit yet — we emit manually below
            activeListener,
          );
        }

        // For group header clicks, also set doublexAxisKey with the group values (e.g., "2025")
        if (this.selectedGroupHeaders.size > 0 && this.data?.doublexAxisKey) {
          const groupValues = Array.from(this.selectedGroupHeaders);
          this.globalFilterService.updateFilterSelection(
            this.data.doublexAxisKey,
            groupValues,
            false,
            widgetId,
            false,
            activeListener,
          );
        }
      } else {
        // Deselection: include self so table resets itself
        const listenerParts = rawListenerStr.split(',').map((s: string) => s.trim()).filter(Boolean);
        if (selfId && !listenerParts.includes(selfId)) {
          listenerParts.push(selfId);
        }
        activeListener = listenerParts.length > 0 ? listenerParts.join(',') : undefined;

        // Deselection: clear the filter keys
        if (labelKey) {
          this.globalFilterService.deleteKey(labelKey, false);
        }
        if (this.data?.doublexAxisKey) {
          this.globalFilterService.deleteKey(this.data.doublexAxisKey, false);
        }

        // Use resetFilterSelection pattern as in updateFilterFromSelectedCells
        if (labelKey) {
          this.globalFilterService.resetFilterSelection(labelKey, false, activeListener);
        }
      }

      // Ensure buildCategoryFilterData uses the correctly adjusted listener list
      const listenerArray = activeListener ? activeListener.split(',') : [];
      const filterData = buildCategoryFilterData(selectedLegends, widgetId, listenerArray, labelKey);

      // Attach requestParams so listening charts send them in API payload
      const requestParams: Record<string, any> = {};
      if (selectedLegends.length > 0) {
        if (labelKey) {
          requestParams[labelKey] = selectedLegends;
        }
        if (this.selectedGroupHeaders.size > 0 && this.data?.doublexAxisKey) {
          requestParams[this.data.doublexAxisKey] = Array.from(this.selectedGroupHeaders);
        }
      }
      (filterData as any).requestParams = requestParams;

      (this.globalFilterService as any).filterSubject.next(filterData);
    } else {
      // Regular cell-based filter
      const listener = Array.isArray(filterListeners) ? filterListeners.join(',') : filterListeners;
      const filterData = {
        legendSync: true,
        selectedLegends: selectedLegends,
        selectedIndices: [],
        selectedColors: [],
        seriesNames: [],
        sourceWidgetId: widgetId,
        filterListener: listener,
        isCategoryFilter: false,
      };
      (this.globalFilterService as any).filterSubject.next(filterData);
    }
  }

  // Handle external legend filter events from charts
  protected handleExternalLegendFilter(event: {
    sourceWidgetId: string;
    selectedLegends: string[];
    selectedIndices: number[];
    selectedColors: string[];
    targetWidgetIds: string[];
    visitedWidgetIds: string[];
    isCategoryFilter?: boolean;
  }): void {
    const widgetId = this.data?.widgetId;

    // Check if this widget should respond to the filter
    if (!widgetId || !event.targetWidgetIds.includes(widgetId)) {
      return;
    }

    // Don't respond if already visited (prevents infinite loops)
    if (event.visitedWidgetIds.includes(widgetId)) {
      return;
    }

    // [REMOVED] Clearing cell selection here was preventing additive filtering between charts and tables.
    // By removing this, table selections (e.g. customer_name) will persist when chart legends (e.g. segment) are clicked.

    // Handle reset signal (empty legends = show all)
    if (event.selectedLegends.length === 0 && event.selectedIndices.length === 0) {
      this.activeLegendSyncFilter = null;
      this.dataSource = [...this.originalDataSource];
      this.selectedGroupHeaders.clear();
      this.selectedColumnHeaders.clear();
      this.pageIndex = 0;
      this.setPagedData();
      // this.paginatorComp?.updatePagination(this.dataSource.length, this.pageIndex, this.pageSize);
      return;
    }

    // Store the active filter so it can be re-applied after a data reload
    this.activeLegendSyncFilter = { ...event, isCategoryFilter: event.isCategoryFilter || false };

    // Clear any previous header highlights
    this.selectedGroupHeaders.clear();
    this.selectedColumnHeaders.clear();

    const matchMode = this.getMatchMode();

    // Check if this is a category-based filter (from table header clicks like "2023", "Q4")
    if (event.isCategoryFilter) {
      // Match columns using normalized key (e.g. "Q1_2025" → "Q1 2025") with exact mode
      // to avoid false positives where short labels like "Q1" match "Q1 2025" via contains
      const matchingColumns = this.displayedColumns.filter((col) => {
        const normalizedCol = col.replace(/_/g, ' ');
        return event.selectedLegends.some(
          (legend) =>
            matchValue(col, legend, 'exact') ||
            matchValue(normalizedCol, legend, 'exact'),
        );
      });

      // Auto-select group header only when ALL its columns are matched
      // e.g. clicking year "2025" sends Q1-Q4 2025 → all 4 columns match → highlight "2025" group
      // but clicking "Q1 2025" sends only 1 → not all columns → don't highlight "2025" group
      for (const group of this.headerGroups) {
        if (!group.label || !group.columns?.length) continue;
        const allColumnsMatched = group.columns.every((col) => matchingColumns.includes(col));
        if (allColumnsMatched) {
          this.selectedGroupHeaders.add(group.label);
        }
      }

      if (matchingColumns.length > 0) {
        matchingColumns.forEach((col) => this.selectedColumnHeaders.add(col));
        // Don't filter rows for category-based selection, just highlight headers
        return;
      }
    }

    // Filter table data based on selected legends using crossFilterColumns config
    const crossFilterColumns: string[] = this.data?.crossFilterColumns || [];

    const filteredData = this.originalDataSource.filter((row) => {
      // Get values from all configured crossFilterColumns
      const rowValues = crossFilterColumns.map((colKey) =>
        (row[colKey] || '').toString().trim().toUpperCase(),
      );

      // Check if any of the row's crossFilterColumn values match any of the selected legends
      return event.selectedLegends.some((legend) => {
        const legendUpper = legend.trim().toUpperCase();
        return rowValues.includes(legendUpper);
      });
    });

    // Update dataSource with filtered data (or show all if no matches)
    this.dataSource = filteredData.length > 0 ? filteredData : [...this.originalDataSource];
    this.pageIndex = 0;
    this.setPagedData();
    // this.paginatorComp?.updatePagination(this.dataSource.length, this.pageIndex, this.pageSize);
  }

  // Clears all cell/header highlight selection state
  protected clearAllSelections(): void {
    this.selectedCells = [];
    this.selectedGroupHeaders.clear();
    this.selectedColumnHeaders.clear();
  }

  // Returns true when the filter event represents a full reset (no active filters)
  protected isResetFilter(filters?: any): boolean {
    if (!filters) return true;
    const hasQuery = Object.keys(filters?.queryParams || {}).length > 0;
    const hasRequest = Object.keys(filters?.requestParams || {}).length > 0;
    return !hasQuery && !hasRequest;
  }

  // Load table data from mock JSON file (for POC - uses GET request)
  protected loadAndRenderChart(filters?: any): void {
    console.log('filters', filters);
    this.isLoading = true;

    // Clear highlight selections on global reset so highlighted cells don't linger
    if (this.isResetFilter(filters)) {
      this.clearAllSelections();
    }

    // 1. Inline Data
    if (this.data?.tableData) {
      this.initTableWithData(this.data.tableData);
      return;
    }

    // 2. Determine URL (API or Mock)
    const url = this.data?.apiUrl || this.data?.mockDataUrl;

    // 3. Data Fetching
    if (url) {
      // Determine method (POST for API, GET if .json)
      let method = 'post';
      if (this.data?.method) {
        method = this.data.method;
      } else if (url.endsWith('.json')) {
        method = 'get';
      }

      this.configService
        .getConfig(url, this.data, filters, method, true)
        .pipe(takeUntil(this.unSubscribe$))
        .subscribe({
          next: (res: any) => this.processApiResponse(res),
          error: (err) => {
            this.isLoading = false;
            console.error('Error loading table data', err);
          },
        });
    } else {
      this.isLoading = false;
    }
  }


  protected processApiResponse(res: any): void {
    let tableData: any[] = [];

    // Set pageSize from config if provided
    if (this.data?.pageSize) {
      this.pageSize = this.data.pageSize;
    }

    console.log(
      '[Base GenericTableComponent] processApiResponse - widgetId:',
      this.widgetId,
      'raw response keys:',
      res ? Object.keys(res) : null,
    );

    const dataKey = this.data?.dataKey;
    if (dataKey && Array.isArray(res?.[dataKey])) {
      tableData = res[dataKey];
    }
    // Handle new nested structure with pivoting
    else if (res?.customer) {
      const map = new Map<string, any>();

      res.customer.forEach((item: any) => {
        const key = `${item.customer_name}_${item.channel_type || ''}`;
        if (!map.has(key)) {
          map.set(key, {
            customer_name: item.customer_name,
            channel_type: item.channel_type,
            _total: 0,
          });
        }
        const row = map.get(key);

        if (item.year) {
          row[item.year] = this.formatCurrency(item.sumOfTotalSales);
          row._total += item.sumOfTotalSales || 0;
        }
      });

      tableData = Array.from(map.values()).map((row) => {
        row['TOTAL'] = this.formatCurrency(row._total);
        delete row._total;
        return row;
      });
    } else {
      tableData = res?.options || res?.data || res || [];
    }

    this.isLoading = false;

    // Legacy/Specific Logic (Safe guard)
    if (tableData.length > 0 && 'proposed_option_price' in tableData[0]) {
      tableData = tableData.map((row: any) => ({
        ...row,
        difference: Number((row.proposed_option_price - row.current_option_price).toFixed(3)),
      }));
      tableData.sort((a: any, b: any) => b.difference - a.difference);
    }

    this.originalDataSource = tableData;
    this.dataSource = [...this.originalDataSource];

    if (!!this.data?.isPagination) {
      this.pageIndex = 0; // Reset page on new data
      this.setPagedData();
    }

    this.displayedColumns = this.data?.headerColumns?.map((col: any) => col.key) || [];
    this.columnLabels = Object.fromEntries(
      this.data?.headerColumns?.map((col: any) => [col.key, col.label]) || [],
    );

    this.initColumnWidths();

    console.log(
      '[Base GenericTableComponent] processApiResponse - widgetId:',
      this.widgetId,
      'displayedColumns:',
      this.displayedColumns,
      'headerColumns:',
      this.data?.headerColumns,
      'headerGroups:',
      this.data?.headerGroups,
    );

    this.initGroupedHeaders();

    // Re-apply active legendSync filter if data was reloaded while a filter was active
    if (this.activeLegendSyncFilter) {
      this.handleExternalLegendFilter(this.activeLegendSyncFilter);
    }

    setTimeout(() => {
      this.paginatorComp?.updatePagination(this.dataSource.length, this.pageIndex);
    }, 200);
  }


  protected formatCurrency(value: number): string {
    if (value === undefined || value === null) return '-';
    if (value >= 1000000000) return (value / 1000000000).toFixed(1) + 'B';
    if (value >= 1000000) return (value / 1000000).toFixed(1) + 'M';
    if (value >= 1000) return (value / 1000).toFixed(1) + 'K';
    return value.toFixed(1);

  }

  onPageChange(event: any) {
    // Accept generic event (some paginator implementations emit a native Event)
    // and defensively read pageIndex/pageSize fields when available.
    const pEvent: any = event as any;
    this.pageSize = pEvent?.pageSize ?? this.pageSize;
    this.pageIndex = pEvent?.pageIndex ?? this.pageIndex;
    this.setPagedData();
  }

  // ===== PAGINATION USING UTILITY =====
  protected setPagedData() {
    // Use paginate utility (1-indexed internally, so add 1 to pageIndex)
    this.pagedData = paginate(this.dataSource, this.pageIndex + 1, this.pageSize);
  }

  // ─── Column Resize ────────────────────────────────────────────────────────

  protected initColumnWidths(): void {
    const configured: { [key: string]: number } = this.data?.columnWidths || {};
    for (const col of this.displayedColumns || []) {
      if (this.columnWidths[col] === undefined) {
        this.columnWidths[col] = configured[col] ?? this.DEFAULT_COL_WIDTH;
      }
    }
  }

  private readNumericConfig(keys: string[]): number | null {
    for (const key of keys) {
      const direct = Number(this.data?.[key]);
      if (Number.isFinite(direct)) {
        return direct;
      }
      const nested = Number(this.data?.chartConfig?.[key]);
      if (Number.isFinite(nested)) {
        return nested;
      }
    }
    return null;
  }

  protected getMinColumnWidth(): number {
    const configuredMinWidth = this.readNumericConfig([
      'minColumnWidth',
      'minimumColumnWidth',
      'minColWidth',
      'minWidth',
    ]);
    if (configuredMinWidth !== null) {
      return Math.max(0, configuredMinWidth);
    }
    return this.MIN_COL_WIDTH;
  }

  protected getCollapsedColumnWidth(): number {
    const configuredCollapsedWidth = this.readNumericConfig([
      'collapsedColumnWidth',
      'collapseColumnWidth',
      'collapsedColWidth',
      'collapsedWidth',
    ]);
    if (configuredCollapsedWidth !== null) {
      return Math.max(this.getMinColumnWidth(), configuredCollapsedWidth);
    }
    return Math.max(this.getMinColumnWidth(), this.DEFAULT_COLLAPSED_COL_WIDTH);
  }

  // ─── Freeze Column Helpers (delegated to shared utility) ────────────────────

  isFrozenColumn(column: string): boolean {
    return _isFrozenColumn(this.data, this.displayedColumns, column);
  }

  isLastFrozenColumn(column: string): boolean {
    return _isLastFrozenColumn(this.data, this.displayedColumns, column);
  }

  getFrozenColumnLeft(column: string): number | null {
    return _getFrozenColumnLeft(this.data, this.displayedColumns, this.columnWidths, this.DEFAULT_COL_WIDTH, column);
  }

  isFrozenGroup(group: { columns?: string[] }): boolean {
    return _isFrozenGroup(this.data, this.displayedColumns, group);
  }

  getFrozenGroupLeft(group: { columns?: string[] }): number | null {
    return _getFrozenGroupLeft(this.data, this.displayedColumns, this.columnWidths, this.DEFAULT_COL_WIDTH, group);
  }

  isFrozenBodyRow(rowIndex: number): boolean {
    const rowCount = this.data?.isPagination ? this.pagedData.length : this.dataSource.length;
    return _isFrozenBodyRow(this.data, rowIndex, rowCount);
  }

  getFrozenBodyRowTop(rowIndex: number): number | null {
    const rowCount = this.data?.isPagination ? this.pagedData.length : this.dataSource.length;
    return _getFrozenBodyRowTop(this.data, rowIndex, rowCount);
  }

  getFrozenBodyRowBottom(rowIndex: number): number | null {
    const rowCount = this.data?.isPagination ? this.pagedData.length : this.dataSource.length;
    return _getFrozenBodyRowBottom(this.data, rowIndex, rowCount);
  }

  onResizeStart(event: MouseEvent, column: string): void {
    event.preventDefault();
    event.stopPropagation();
    this.suppressNextHeaderClick = true;
    this.resizingCol = column;
    this.resizeStartX = event.clientX;
    this.resizeStartWidth = this.columnWidths[column] ?? this.DEFAULT_COL_WIDTH;
    this.attachResizeListeners();
  }

  onHeaderMouseDown(event: MouseEvent, column: string): void {
    const target = event.currentTarget as HTMLElement | null;
    if (!target) return;

    const rect = target.getBoundingClientRect();
    const distanceFromRightEdge = rect.right - event.clientX;

    if (distanceFromRightEdge <= 16) {
      this.onResizeStart(event, column);
    }
  }

  private attachResizeListeners(): void {
    if (typeof document === 'undefined') return;
    document.addEventListener('mousemove', this.documentMouseMoveHandler);
    document.addEventListener('mouseup', this.documentMouseUpHandler);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  }

  private detachResizeListeners(): void {
    if (typeof document === 'undefined') return;
    document.removeEventListener('mousemove', this.documentMouseMoveHandler);
    document.removeEventListener('mouseup', this.documentMouseUpHandler);
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
  }

  @HostListener('document:mousemove', ['$event'])
  onMouseMove(event: MouseEvent): void {
    if (!this.resizingCol) return;
    const delta = event.clientX - this.resizeStartX;
    const newWidth = Math.max(this.getMinColumnWidth(), this.resizeStartWidth + delta);
    this.columnWidths[this.resizingCol] = newWidth;
  }

  @HostListener('document:mouseup')
  onMouseUp(): void {
    this.resizingCol = null;
    this.detachResizeListeners();
    setTimeout(() => {
      this.suppressNextHeaderClick = false;
    }, 0);
  }

  filterByLegend(legendType: string | null): void {
    console.log('Clicked legendType:', legendType);
    console.log('Active legend before click:', this.activeLegend);

    if (this.activeLegend === legendType) {
      console.log('Same legend clicked again. Resetting filter.');
      this.activeLegend = null;
      this.dataSource = [...this.originalDataSource];
    } else {
      this.activeLegend = legendType;
      if (legendType) {
        console.log('Filtering data for legendType:', legendType);
        const filteredData = this.originalDataSource.filter(
          (row) =>
            (row.option_impact_drivers || '').trim().toLowerCase() ===
            (legendType || '').trim().toLowerCase(),
        );

        console.log('Filtered data:', filteredData);
        this.dataSource = filteredData;
      } else {
        console.log('LegendType is null, resetting dataSource to original.');
        this.dataSource = [...this.originalDataSource];
      }
    }

    console.log('DataSource after filtering:', this.dataSource);
    this.pageIndex = 0;
    this.setPagedData();
    // this.paginatorComp?.updatePagination(this.dataSource.length, this.pageIndex, this.pageSize);
  }

  ngOnDestroy(): void {
    this.detachResizeListeners();
    this.unSubscribe$.next();
    this.unSubscribe$.complete();
    this.legendFilterSub?.unsubscribe();
  }
}

